// ─── Import d'un fichier .ics ─────────────────────────────────────
// Porté de MoPCal (`services/export.js`, `parserICS`). Seule fonction d'ENTRÉE
// de l'export : sans elle, un calendrier exporté ne revient jamais.
//
// Deux détails repris parce qu'ils font la différence :
//   • le DÉPLIAGE des lignes repliées (RFC 5545 : une ligne peut continuer sur
//     la suivante précédée d'un espace) — sans lui, un titre long est tronqué ;
//   • la CATÉGORIE relue dans l'UID que nous écrivons à l'export, donc un
//     aller-retour export → import la préserve.
import { ajouterEvenement } from './evenements';

const pad = (n) => String(n).padStart(2, '0');

const decodeICS = (s) => String(s || '')
  .replace(/\\n/gi, '\n').replace(/\\,/g, ',').replace(/\\;/g, ';').replace(/\\\\/g, '\\')
  .trim();

// '20260613T103000' | '…Z' (UTC) | '20260613' (journée) → { date, heure:'HHhMM'|'' }
function parseDT(val) {
  if (!val) return null;
  const m = String(val).match(/(\d{4})(\d{2})(\d{2})(?:T(\d{2})(\d{2})(\d{2})?(Z)?)?/);
  if (!m) return null;
  if (!m[4]) return { date: `${m[1]}-${m[2]}-${m[3]}`, heure: '' };
  if (m[7] === 'Z') {                       // UTC → heure locale de l'appareil
    const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5]));
    return { date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
             heure: `${pad(d.getHours())}h${pad(d.getMinutes())}` };
  }
  return { date: `${m[1]}-${m[2]}-${m[3]}`, heure: `${m[4]}h${m[5]}` };
}

// Texte .ics → [{ date, heure, heureFin, titre, lieu, note, categorie, uid }]
export function parserICS(texte) {
  if (!texte) return [];
  const lignes = String(texte)
    .replace(/\r\n/g, '\n').replace(/\r/g, '\n')
    .replace(/\n[ \t]/g, '')                // déplie les lignes repliées
    .split('\n');

  const bruts = [];
  let cur = null;
  for (const ligne of lignes) {
    const l = ligne.trim();
    if (l === 'BEGIN:VEVENT') { cur = {}; continue; }
    if (l === 'END:VEVENT') { if (cur) bruts.push(cur); cur = null; continue; }
    if (!cur) continue;
    const i = ligne.indexOf(':');
    if (i === -1) continue;
    const nom = ligne.slice(0, i).split(';')[0].toUpperCase();
    const val = ligne.slice(i + 1);
    if (nom === 'DTSTART') cur._debut = val;
    else if (nom === 'DTEND') cur._fin = val;
    else if (nom === 'UID') cur.uid = val.trim();
    else if (nom === 'SUMMARY') cur.titre = decodeICS(val);
    else if (nom === 'LOCATION') cur.lieu = decodeICS(val);
    else if (nom === 'DESCRIPTION') cur.note = decodeICS(val);
  }

  return bruts.map(e => {
    const d = parseDT(e._debut);
    if (!d) return null;
    const f = parseDT(e._fin);
    // Catégorie encodée à l'export (mop-<ref>-cat<id>-… ou mopcal-… pour les
    // fichiers produits par MoPCal) : un aller-retour ne la perd pas.
    const cat = String(e.uid || '').match(/^mop(?:cal)?-[a-zA-Z0-9]+-cat([a-z]+)-/);
    return {
      date: d.date,
      heure: d.heure || '',
      heureFin: (f && f.date === d.date && f.heure) ? f.heure : '',
      titre: e.titre || 'Événement',
      lieu: e.lieu || '',
      note: e.note || '',
      categorie: cat ? cat[1] : '',
      uid: e.uid || '',
    };
  }).filter(Boolean);
}

// Crée les événements dans le calendrier. Renvoie { ok, echecs }.
// On n'interrompt PAS au premier échec : sur 200 lignes, une seule mauvaise ne
// doit pas faire perdre les 199 autres.
export async function importerEvenements(calId, evenements, auteur) {
  let ok = 0, echecs = 0;
  for (const e of (evenements || [])) {
    try {
      await ajouterEvenement(calId, e.date, {
        titre: e.titre, heure: e.heure || '', heureFin: e.heureFin || '',
        lieu: e.lieu || '', note: e.note || '',
        categorie: e.categorie || 'autre', type: 'simple',
        creePar: auteur?.uid || null, creeParNom: auteur?.nom || '',
        importe: true,                      // trace : distinguer d'une saisie
      });
      ok++;
    } catch { echecs++; }
  }
  return { ok, echecs };
}
